"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { BODY_TYPES, SIZES, FITS } from "@/lib/catalog";

interface UserProfile {
  height: number;
  heightUnit: "cm" | "ft";
  weight: number;
  weightUnit: "kg" | "lb";
  bodyType: string;
  topSize: string;
  bottomSize: string;
  fitPreference: string;
  skinTone: string;
  preferredColors: string[];
  fitNotes: string;
}

const SKIN_TONES = [
  { label: "Fair", value: "fair", color: "#F8D5C2" },
  { label: "Light", value: "light", color: "#E8C4A0" },
  { label: "Medium", value: "medium", color: "#C4986E" },
  { label: "Olive", value: "olive", color: "#A67C52" },
  { label: "Tan", value: "tan", color: "#8B5A3C" },
  { label: "Deep", value: "deep", color: "#5C3A21" },
];

const DEFAULT_PROFILE: UserProfile = {
  height: 170,
  heightUnit: "cm",
  weight: 65,
  weightUnit: "kg",
  bodyType: "Regular",
  topSize: "M",
  bottomSize: "M",
  fitPreference: "Regular",
  skinTone: "medium",
  preferredColors: [],
  fitNotes: "",
};

export default function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile>(DEFAULT_PROFILE);
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem("dressingroom-profile");
    if (raw) {
      try {
        setProfile({ ...DEFAULT_PROFILE, ...JSON.parse(raw) });
      } catch { /* ignore */ }
    }
    setLoaded(true);
  }, []);

  function update<K extends keyof UserProfile>(key: K, value: UserProfile[K]) {
    setProfile((p) => ({ ...p, [key]: value }));
    setSaved(false);
  }

  function save() {
    localStorage.setItem("dressingroom-profile", JSON.stringify(profile));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  if (!loaded) return null;

  return (
    <div className="flex flex-col flex-1">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0F0F0F]/80 border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <span className="text-xl font-bold tracking-tight">
              Dressing<span className="text-[#FF6B35]">Room</span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link href="/catalog" className="text-sm text-gray-400 hover:text-white transition-colors">
              Catalog
            </Link>
            <Link href="/profile" className="text-sm text-white font-medium">
              My Profile
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12 w-full">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Your Profile</h1>
        <p className="text-gray-400 mb-10">
          These measurements power the fit engine. Saved on your device only.
        </p>

        <div className="space-y-8">
          {/* Height */}
          <div>
            <label className="block text-sm font-medium mb-3">Height</label>
            <div className="flex gap-3">
              <input
                type="number"
                value={profile.height}
                onChange={(e) => update("height", parseFloat(e.target.value) || 0)}
                className="flex-1 h-11 px-4 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
              />
              <div className="flex rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden">
                {(["cm", "ft"] as const).map((u) => (
                  <button
                    key={u}
                    onClick={() => {
                      if (u === "ft" && profile.heightUnit === "cm") {
                        update("height", Math.round(profile.height / 30.48 * 10) / 10);
                      } else if (u === "cm" && profile.heightUnit === "ft") {
                        update("height", Math.round(profile.height * 30.48));
                      }
                      update("heightUnit", u);
                    }}
                    className={`px-4 h-11 text-sm font-medium transition-colors ${
                      profile.heightUnit === u ? "bg-[#FF6B35] text-white" : "text-gray-400 hover:text-white"
                    }`}
                  >
                    {u === "cm" ? "cm" : "ft"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Weight */}
          <div>
            <label className="block text-sm font-medium mb-3">Weight</label>
            <div className="flex gap-3">
              <input
                type="number"
                value={profile.weight}
                onChange={(e) => update("weight", parseFloat(e.target.value) || 0)}
                className="flex-1 h-11 px-4 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
              />
              <div className="flex rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden">
                {(["kg", "lb"] as const).map((u) => (
                  <button
                    key={u}
                    onClick={() => {
                      if (u === "lb" && profile.weightUnit === "kg") {
                        update("weight", Math.round(profile.weight * 2.20462));
                      } else if (u === "kg" && profile.weightUnit === "lb") {
                        update("weight", Math.round(profile.weight / 2.20462));
                      }
                      update("weightUnit", u);
                    }}
                    className={`px-4 h-11 text-sm font-medium transition-colors ${
                      profile.weightUnit === u ? "bg-[#FF6B35] text-white" : "text-gray-400 hover:text-white"
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Body Type */}
          <div>
            <label className="block text-sm font-medium mb-3">Body Type</label>
            <div className="flex flex-wrap gap-2">
              {BODY_TYPES.map((bt) => (
                <button
                  key={bt}
                  onClick={() => update("bodyType", bt)}
                  className={`px-5 h-11 rounded-full text-sm font-medium transition-colors ${
                    profile.bodyType === bt
                      ? "bg-[#FF6B35] text-white"
                      : "bg-[#1A1A1A] text-gray-400 border border-[#2A2A2A] hover:text-white"
                  }`}
                >
                  {bt}
                </button>
              ))}
            </div>
          </div>

          {/* Sizes */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-3">Usual Top Size</label>
              <div className="flex flex-wrap gap-2">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    onClick={() => update("topSize", s)}
                    className={`w-11 h-11 rounded-full text-sm font-medium transition-colors ${
                      profile.topSize === s
                        ? "bg-[#FF6B35] text-white"
                        : "bg-[#1A1A1A] text-gray-400 border border-[#2A2A2A] hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-3">Usual Bottom Size</label>
              <div className="flex flex-wrap gap-2">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    onClick={() => update("bottomSize", s)}
                    className={`w-11 h-11 rounded-full text-sm font-medium transition-colors ${
                      profile.bottomSize === s
                        ? "bg-[#FF6B35] text-white"
                        : "bg-[#1A1A1A] text-gray-400 border border-[#2A2A2A] hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Fit Preference */}
          <div>
            <label className="block text-sm font-medium mb-3">Fit Preference</label>
            <div className="flex flex-wrap gap-2">
              {FITS.map((f) => (
                <button
                  key={f}
                  onClick={() => update("fitPreference", f)}
                  className={`px-5 h-11 rounded-full text-sm font-medium transition-colors ${
                    profile.fitPreference === f
                      ? "bg-[#FF6B35] text-white"
                      : "bg-[#1A1A1A] text-gray-400 border border-[#2A2A2A] hover:text-white"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Skin Tone */}
          <div>
            <label className="block text-sm font-medium mb-3">Skin Tone</label>
            <div className="flex flex-wrap gap-3">
              {SKIN_TONES.map((st) => (
                <button
                  key={st.value}
                  onClick={() => update("skinTone", st.value)}
                  className={`flex flex-col items-center gap-1.5 group`}
                >
                  <div
                    className={`w-12 h-12 rounded-full border-2 transition-all ${
                      profile.skinTone === st.value
                        ? "border-[#FF6B35] scale-110"
                        : "border-[#2A2A2A] group-hover:border-gray-600"
                    }`}
                    style={{ backgroundColor: st.color }}
                  />
                  <span className={`text-xs ${profile.skinTone === st.value ? "text-white" : "text-gray-500"}`}>
                    {st.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Fit Notes */}
          <div>
            <label className="block text-sm font-medium mb-3">
              Fit Notes <span className="text-gray-500">(optional)</span>
            </label>
            <textarea
              value={profile.fitNotes}
              onChange={(e) => update("fitNotes", e.target.value)}
              placeholder="e.g., broad shoulders, long arms, prefer looser fit..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] text-white focus:border-[#FF6B35] focus:outline-none transition-colors resize-none"
            />
          </div>

          {/* Save */}
          <div className="flex items-center gap-4 pt-4">
            <button
              onClick={save}
              className="h-11 px-8 rounded-full bg-[#FF6B35] text-white text-sm font-semibold hover:bg-[#ff7d4e] transition-colors shadow-lg shadow-[#FF6B35]/20"
            >
              Save Profile
            </button>
            {saved && (
              <span className="text-sm text-green-400 font-medium animate-pulse">
                ✓ Saved
              </span>
            )}
            <Link
              href="/catalog"
              className="ml-auto h-11 px-6 inline-flex items-center justify-center rounded-full bg-[#1A1A1A] border border-[#2A2A2A] text-white text-sm font-semibold hover:bg-[#222] transition-colors"
            >
              Browse Catalog →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
